/**
 * Pure domain logic for Zero-U lateral equipment segregation,
 * side channel state computation, and inspector tab clamping.
 */

import { isZeroUEquipment } from './rackMetrics.logic.ts';

export interface ZeroUSideColumn {
  side: 'Left' | 'Right';
  equipment: any | null;
  equipments: any[];
  isGhost: boolean;
  droppableId: string;
  label: string;
  feedDefault: string;
  phaseDefault: string;
}

export interface CategorizedEquipment {
  mounted: any[];
  zeroU: any[];
  zeroUBySide: {
    left: any[];
    right: any[];
    other: any[];
  };
}

/**
 * Categorizes a flat equipment list into standard EIA-310 horizontal mounted units
 * and lateral Zero-U infrastructure (PDUs, ATS, vertical monitors).
 */
export function categorizeEquipment(equipmentList: any[] | null | undefined): CategorizedEquipment {
  const list = Array.isArray(equipmentList) ? equipmentList : [];
  const mounted: any[] = [];
  const zeroU: any[] = [];
  const left: any[] = [];
  const right: any[] = [];
  const other: any[] = [];

  for (const eq of list) {
    if (!eq) continue;
    if (isZeroUEquipment(eq)) {
      zeroU.push(eq);
      const rawMount = String(eq.mounting_position || '').toLowerCase();
      if (rawMount.includes('left') || eq.attributes?.feed_name === 'Feed A') {
        left.push(eq);
      } else if (rawMount.includes('right') || eq.attributes?.feed_name === 'Feed B') {
        right.push(eq);
      } else {
        other.push(eq);
      }
    } else {
      mounted.push(eq);
    }
  }

  return {
    mounted,
    zeroU,
    zeroUBySide: {
      left,
      right,
      other,
    },
  };
}

/**
 * Computes lateral Zero-U column state for Left (Feed A) and Right (Feed B) channels.
 */
export function getZeroUSideColumns(equipmentList: any[] | null | undefined): {
  left: ZeroUSideColumn;
  right: ZeroUSideColumn;
} {
  const { zeroUBySide } = categorizeEquipment(equipmentList);
  const leftEqs = zeroUBySide.left;
  const rightEqs = zeroUBySide.right;
  const leftEq = leftEqs[0] || null;
  const rightEq = rightEqs[0] || null;

  return {
    left: {
      side: 'Left',
      equipment: leftEq,
      equipments: leftEqs,
      isGhost: leftEqs.length === 0,
      droppableId: 'zero-u-Left',
      label: leftEq ? leftEq.name : 'PDU A (Ввод A)',
      feedDefault: 'Feed A',
      phaseDefault: 'phase_a',
    },
    right: {
      side: 'Right',
      equipment: rightEq,
      equipments: rightEqs,
      isGhost: rightEqs.length === 0,
      droppableId: 'zero-u-Right',
      label: rightEq ? rightEq.name : 'PDU B (Ввод B)',
      feedDefault: 'Feed B',
      phaseDefault: 'phase_b',
    },
  };
}

/**
 * Clamps inspector leftTab to ensure consistent states and prevent empty blackouts.
 * - If 'ports' or 'device' is active but no device is selected -> clamps to 'passport'.
 * - If 'catalog' is active but mounting mode is inactive -> clamps to 'passport'.
 */
export function clampLeftTab(
  currentTab: string,
  selectedEq: any | null | undefined,
  isMountingMode: boolean = false
): string {
  if ((currentTab === 'ports' || currentTab === 'device') && !selectedEq) {
    return 'passport';
  }
  if (currentTab === 'catalog' && !isMountingMode) {
    return 'passport';
  }
  return currentTab;
}

/**
 * Finds the continuous free block of units with the largest height.
 */
export function findLargestFreeBlock(
  freeUBlocks: Array<{ start: number; size: number }> | null | undefined
): { start: number; size: number } | null {
  if (!Array.isArray(freeUBlocks) || freeUBlocks.length === 0) return null;
  return freeUBlocks.reduce((largest, current) => {
    if (!current) return largest;
    if (!largest) return current;
    return current.size > largest.size ? current : largest;
  }, null as { start: number; size: number } | null);
}

/**
 * Filters equipment by textual query and category.
 */
export function filterEquipment<T extends {
  name?: string;
  equipment_type?: string;
  model?: string;
  manufacturer?: string;
  asset_tag?: string | null;
  serial_number?: string | null;
  u_position?: number | null;
  is_zero_u?: boolean;
}>(
  list: T[],
  searchQuery: string = '',
  category: string = 'all'
): T[] {
  if (!Array.isArray(list)) return [];
  let result = list;

  if (category && category !== 'all') {
    if (category === 'server') {
      result = result.filter(eq => eq.equipment_type === 'server');
    } else if (category === 'network') {
      result = result.filter(eq => ['switch', 'router', 'firewall', 'patch_panel'].includes(eq.equipment_type || ''));
    } else if (category === 'power') {
      result = result.filter(eq => ['pdu', 'ups'].includes(eq.equipment_type || '') || eq.is_zero_u);
    } else if (category === 'storage') {
      result = result.filter(eq => eq.equipment_type === 'storage');
    }
  }

  const q = searchQuery.trim().toLowerCase();
  if (q) {
    result = result.filter(eq => {
      const nameMatch = eq.name?.toLowerCase().includes(q);
      const modelMatch = eq.model?.toLowerCase().includes(q);
      const mfrMatch = eq.manufacturer?.toLowerCase().includes(q);
      const tagMatch = eq.asset_tag?.toLowerCase().includes(q);
      const snMatch = eq.serial_number?.toLowerCase().includes(q);
      const uMatch = eq.u_position != null && (String(eq.u_position) === q || `u${eq.u_position}`.includes(q));
      return Boolean(nameMatch || modelMatch || mfrMatch || tagMatch || snMatch || uMatch);
    });
  }

  return result;
}

/**
 * Returns pre-filled initial form data for mounting a Zero-U PDU
 * into a lateral side channel ('Left' = Feed A, 'Right' = Feed B).
 * Automatically de-duplicates default names (e.g. PDU-A-2) if PDU-A already exists.
 */
export function getGhostPduFormDefaults(side: 'Left' | 'Right', existingEquipment?: any[]) {
  const isRight = String(side || '').toLowerCase().includes('right');
  const normalizedSide: 'Left' | 'Right' = isRight ? 'Right' : 'Left';
  const baseName = normalizedSide === 'Left' ? 'PDU-A' : 'PDU-B';

  let resolvedName = baseName;
  if (existingEquipment && Array.isArray(existingEquipment)) {
    const existingNames = new Set(
      existingEquipment
        .map(e => (typeof e?.name === 'string' ? e.name.trim().toLowerCase() : ''))
        .filter(Boolean)
    );

    if (existingNames.has(baseName.toLowerCase())) {
      let counter = 2;
      while (existingNames.has(`${baseName}-${counter}`.toLowerCase())) {
        counter += 1;
      }
      resolvedName = `${baseName}-${counter}`;
    }
  }

  return {
    name: resolvedName,
    equipment_type: 'pdu',
    u_height: 0,
    u_position: 0,
    mounting_position: normalizedSide,
    is_zero_u: true,
    weight_kg: 4,
    power_w: 0,
    status: 'Active',
    attributes: JSON.stringify({
      feed_name: normalizedSide === 'Left' ? 'Feed A' : 'Feed B',
      phase: normalizedSide === 'Left' ? 'phase_a' : 'phase_b',
      rated_current_a: 32,
      outlets_count: 24,
      outlet_type: 'C13',
      outlets_spec: [{ count: 24, type: 'C13' }],
    }),
  };
}

/**
 * Validates whether an equipment item is structurally permissible in a Zero-U lateral channel.
 * Prevents accidental moves of 1U-4U servers, switches, or storage into Zero-U slots.
 */
export function canMountInZeroU(equipment: any): boolean {
  if (!equipment || typeof equipment !== 'object') return false;

  // Explicit zero-U flag
  if (equipment.is_zero_u) return true;

  // Equipment height is 0
  if (equipment.u_height === 0) return true;

  // Check known Zero-U equipment types
  const type = String(equipment.equipment_type || '').toLowerCase().trim();
  const zeroUTypes = new Set(['pdu', 'cable_organizer', 'sensor', 'accessory', 'ats', 'busbar']);
  if (zeroUTypes.has(type)) return true;

  return false;
}

